import { useCallback, useEffect, useState } from "react";

interface Answer<P, T> {
  params: P;
  attempt: number;
  /** null when the request failed. */
  data: T | null;
}

/**
 * The server's answer for `params`, asked again whenever they change (or `reload` is called) and
 * not asked at all while they are null. Only the latest request may settle, and nothing is
 * returned until the CURRENT params are answered, so a new filter never shows the previous one's
 * figures under it. `params` and `load` must keep their identity between renders.
 *
 * `replace` puts a newer answer the caller already holds (the day a write returned) in place
 * without asking again.
 */
export function useLatestAnswer<P, T>(
  params: P | null,
  load: (params: P) => Promise<T>,
) {
  const [answer, setAnswer] = useState<Answer<P, T>>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (params === null) return undefined;
    let isLatest = true;
    load(params)
      .then((data) => {
        if (isLatest) setAnswer({ params, attempt, data });
      })
      .catch(() => {
        // The interceptor has already reported the failure; the screen shows that it failed
        // rather than keep the previous answer on screen.
        if (isLatest) setAnswer({ params, attempt, data: null });
      });
    return () => {
      isLatest = false;
    };
  }, [params, load, attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const replace = useCallback(
    (data: T) => {
      if (params !== null) setAnswer({ params, attempt, data });
    },
    [params, attempt],
  );

  const current =
    params !== null && answer?.params === params && answer.attempt === attempt
      ? answer
      : undefined;

  return {
    data: current?.data ?? null,
    isLoading: params !== null && current === undefined,
    isFailed: current !== undefined && current.data === null,
    reload,
    replace,
  };
}
